import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StickyNote } from '@td/shared';
import { del, get, patch, post } from '../lib/api';

const KEY = ['sticky-notes'];

export function useStickyNotes() {
  return useQuery({ queryKey: KEY, queryFn: () => get<StickyNote[]>('/sticky-notes') });
}

type StickyInput = Partial<Pick<StickyNote, 'text' | 'color' | 'x' | 'y' | 'z'>>;

export function useCreateSticky() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: StickyInput) => post<StickyNote>('/sticky-notes', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}

/**
 * Saving a change to one note.
 *
 * Written into the cache rather than refetched. Typing on a sticky note saves
 * as she types, and a refetch per keystroke would fight the cursor — the
 * server's answer arriving mid-word and replacing what she has since typed is
 * exactly the sort of thing that makes a text box feel broken.
 */
export function useUpdateSticky() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...input }: StickyInput & { id: string }) =>
      patch<StickyNote>(`/sticky-notes/${id}`, input),
    onSuccess: (note) => {
      qc.setQueryData<StickyNote[]>(KEY, (old) =>
        (old ?? []).map((n) => (n.id === note.id ? note : n)),
      );
    },
  });
}

export function useDeleteSticky() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/sticky-notes/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });
}
